import {createPrivateMediaStorage} from '../platform/private-media-storage.js';
import {
  createJournalPhoto,
  isOwnedJournalPhotoPath,
  journalPhotoPath
} from './journal-photo.js';

function safeError(error,code){
  if(error instanceof TypeError)throw error;
  const wrapped=new Error(code);
  wrapped.code=String(error?.code||code).slice(0,80);
  wrapped.cause=error;
  return wrapped;
}

export function createJournalMediaStorage({
  client,
  signedUrlTtlSeconds=3600
}={}){
  const media=createPrivateMediaStorage({client,signedUrlTtlSeconds});

  async function upload({photo,file}={}){
    const value=createJournalPhoto(photo);
    const path=journalPhotoPath(value);
    try{
      await media.upload({
        userId:value.userId,
        path,
        file,
        mimeType:value.mimeType,
        sizeBytes:value.sizeBytes,
        // Stable owner/session/photo-id path: retrying the same durable local
        // payload may safely overwrite an object created just before a crash.
        upsert:true
      });
    }catch(error){
      throw safeError(error,'JOURNAL_PHOTO_UPLOAD_FAILED');
    }
    return Object.freeze({ok:true,path});
  }

  async function remove({photo}={}){
    const value=createJournalPhoto(photo);
    if(!value.storagePath)return Object.freeze({ok:true,status:'already_absent'});
    try{
      await media.remove({userId:value.userId,paths:[value.storagePath]});
    }catch(error){
      throw safeError(error,'JOURNAL_PHOTO_DELETE_FAILED');
    }
    return Object.freeze({ok:true,status:'deleted',path:value.storagePath});
  }

  async function signedUrl({photo}={}){
    const value=createJournalPhoto(photo);
    if(!value.storagePath)return null;
    if(!isOwnedJournalPhotoPath(value.storagePath,{
      userId:value.userId,
      sessionId:value.sessionId,
      itemId:value.itemId,
      photoId:value.id
    }))throw new TypeError('Journal photo path is outside the expected scope');
    try{
      return await media.signedUrl({userId:value.userId,path:value.storagePath});
    }catch(error){
      throw safeError(error,'JOURNAL_PHOTO_URL_FAILED');
    }
  }

  return Object.freeze({upload,remove,signedUrl});
}
